import { MenuItem, NonIdealState } from "@blueprintjs/core"
import { ItemRendererProps, Suggest } from "@blueprintjs/select"
import React, { useCallback, useMemo, useState } from "react"
import { FundamentalsPeriodicity } from "../../api/fundamentalsApi"
import { useFundamentalMetrics } from "../../hooks/useFundamentalMetrics"

interface FundamentalMetricSuggestProps {
  company_id?: number
  periodicity: FundamentalsPeriodicity
  value?: string
  onSelect: (metric: string | null) => void
}

export const FundamentalMetricSuggest: React.FC<FundamentalMetricSuggestProps> = ({
  company_id,
  periodicity,
  value,
  onSelect,
}) => {
  const [query, setQuery] = useState("")
  const { metrics, metrics_by_periodicity, isLoading } = useFundamentalMetrics(company_id)

  // Get metrics for selected periodicity
  const availableMetrics = useMemo(() => {
    if (!metrics && !metrics_by_periodicity) return []

    // Use new periodicity-specific data if available, fallback to legacy
    if (metrics_by_periodicity) {
      return metrics_by_periodicity[periodicity]
    }

    // Legacy fallback: return all metrics
    return metrics || []
  }, [metrics, metrics_by_periodicity, periodicity])

  // Filter metrics based on search query
  const filteredMetrics = useMemo(() => {
    if (!query) return availableMetrics
    const q = query.toLowerCase()
    return availableMetrics.filter((m: string) => m.toLowerCase().includes(q))
  }, [availableMetrics, query])

  const renderItem = useCallback(
    (metric: string, { handleClick, modifiers }: ItemRendererProps) => (
      <MenuItem active={modifiers.active} key={metric} onClick={handleClick} text={metric} />
    ),
    [],
  )

  const handleSelect = useCallback(
    (metric: string) => {
      onSelect(metric)
      setQuery("") // Clear search after selection
    },
    [onSelect],
  )

  const placeholder = useMemo(() => {
    if (!company_id) return "Select a company first"
    if (isLoading) return "Loading metrics..."
    if (query && filteredMetrics.length === 0) return `No metrics match "${query}"`
    if (availableMetrics.length === 0) return `No metrics available for ${periodicity} periodicity`
    return "Select a metric"
  }, [company_id, isLoading, query, filteredMetrics, availableMetrics, periodicity])

  return (
    <Suggest<string>
      items={filteredMetrics}
      selectedItem={value ?? null}
      itemRenderer={renderItem}
      onItemSelect={handleSelect}
      onQueryChange={setQuery}
      disabled={!company_id || isLoading || availableMetrics.length === 0}
      inputValueRenderer={(metric: string) => metric}
      inputProps={{ placeholder, large: true }}
      resetOnSelect={false}
      fill
    />
  )
}

export default FundamentalMetricSuggest
